import { AuthResponse } from '../types/api';
import { post } from './http';

const getCookies = (): Record<string, string> => {
    const cookies = document.cookie.split(';');
    const cookieJar: Record<string, string> = {};

    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
        cookieJar[name.trim()] = cookie.substring(eqPos + 1);
    }

    return cookieJar;
};

const getSessionKey = () => {
    const hostname = document.location.hostname;
    const sessionKeyFromHost = new RegExp(/-(?<sid>[0-9a-fA-F]{32})\./g).exec(hostname)?.[1];
    if (sessionKeyFromHost) {
        return sessionKeyFromHost;
    }
    const cookies = getCookies();
    return cookies['sid' + String(hostname.split('.').length - 1)];
};

/**
 * The cookie name legacy uses for the session id: "sid" + the hostname's dot count.
 * `cobrakai.hd-test01.com` -> `sid2`. Must stay in step with `getSessionKey()` above and with
 * `GetCleanHostNameData` in the legacy app (`inc/functions_security.asp:25`).
 */
const getSidCookieName = (hostname: string) => `sid${String(hostname.split('.').length - 1)}`;

/** Injectable for tests — `document.location` is non-configurable in jsdom. */
export type LocationLike = { hostname: string; protocol: string };

/**
 * Persists the session so a full document load (bookmark, refresh, deep link) can restore it
 * via `authorize()` on boot.
 *
 * Writes the same cookie the legacy app writes in `inc/user.asp:215`, so both stacks share one
 * session and "Back to Legacy Site" works without re-authenticating. Deliberately NOT HttpOnly:
 * `getSessionKey()` reads it from `document.cookie`, and legacy does not set HttpOnly either.
 *
 * Before this existed, only the legacy login established a session; logging in through the React
 * form left nothing durable, so every hard navigation bounced back to the login screen (ROBO-276).
 *
 * When legacy is retired this should go away in favour of the JWT alone.
 */
export function persistSessionKey(sessionKey?: string, loc: LocationLike = document.location) {
    if (!sessionKey) {
        return;
    }
    localStorage.setItem('sessionKey', sessionKey);

    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1); // matches legacy's 1-year expiry
    const secure = loc.protocol === 'https:' ? '; SameSite=None; Secure' : '';
    document.cookie =
        `${getSidCookieName(loc.hostname)}=${sessionKey}` +
        `; expires=${expires.toUTCString()}` +
        `; domain=${loc.hostname.toLowerCase()}` +
        `; path=/${secure}`;
}

/**
 * One-time cleanup for ROBO-275: the login form used to write `creds-email` and `creds-password`
 * to localStorage on every keystroke, the password in plaintext. Removing the write does nothing
 * for values already stored in existing browsers — localStorage has no expiry — so this clears
 * them on boot. Safe to delete once enough time has passed that no active browser still holds them.
 */
export function purgeLegacyCredentialStorage() {
    localStorage.removeItem('creds-email');
    localStorage.removeItem('creds-password');
}

/** Clears both halves of the persisted session. Used on logout. */
export function clearPersistedSessionKey(loc: LocationLike = document.location) {
    localStorage.removeItem('sessionKey');
    const secure = loc.protocol === 'https:' ? '; SameSite=None; Secure' : '';
    document.cookie =
        `${getSidCookieName(loc.hostname)}=` +
        `; expires=Thu, 01 Jan 1970 00:00:00 GMT` +
        `; domain=${loc.hostname.toLowerCase()}` +
        `; path=/${secure}`;
}

/** The parts of `window.location` {@link consumeSessionKeyFromUrl} reads. */
export type SessionUrlLocation = LocationLike & {
    search: string;
    pathname: string;
    hash: string;
};

/**
 * The query-string parameter legacy passes a session id in — `?SessionKey=<32 hex>`.
 *
 * Legacy's own `cSessionKey` reads it FIRST, ahead of the host and the cookie
 * (`inc/functions_security.asp:57-59`), and every cross-host hand-off in the app depends on that:
 * `/Login/persist.asp`, `Login/login.asp`'s cross-brand redirect and the impersonation spawn in
 * `Dashboard/dashboard3.asp:95-104` all carry the new session in this parameter, because the tab
 * is landing on a hostname whose cookie jar is empty by definition.
 */
export const SESSION_KEY_PARAM = 'SessionKey';

/**
 * A session id is a 32-character hex guid with no dashes (legacy `ID()`). Exported for the Google
 * return (`google-sso.ts`), which validates the key the same way before persisting it.
 */
export const SESSION_KEY_RE = /^[0-9a-fA-F]{32}$/;

/**
 * Takes a session id off the URL, stores it, and strips it from the address bar. Returns the key
 * when there was a usable one, so the caller can hand it straight to `authorize()`.
 *
 * CONSUMED, NOT MERELY READ, AND IN THAT ORDER: the key is persisted (cookie + localStorage, via
 * {@link persistSessionKey}) BEFORE the parameter is removed, so a session id is never held only
 * by the address bar. It is stripped with `replaceState` rather than a navigation, so it leaves no
 * history entry to go Back to, and does not survive into a refresh, a bookmark, a copied link or a
 * `Referer` header.
 *
 * VALIDATED BEFORE IT IS STORED. Anything that is not a 32-character hex guid is ignored and left
 * in the URL untouched: a malformed value must not overwrite the session the browser already
 * holds, and `authorize()` would reject it anyway.
 *
 * ★ PARITY WITH LEGACY, NOT A NEW DOOR. The same parameter already establishes the same `sid<n>`
 * cookie on the same origins through `persist.asp`, which is how the two stacks share one session
 * for the parallel-run window. It should be retired when legacy is.
 */
export function consumeSessionKeyFromUrl(
    loc: SessionUrlLocation = window.location,
    history: Pick<History, 'state' | 'replaceState'> = window.history
): string | undefined {
    let params: URLSearchParams;
    try {
        params = new URLSearchParams(loc.search);
    } catch {
        return undefined;
    }
    const key = params.get(SESSION_KEY_PARAM);
    if (!key || !SESSION_KEY_RE.test(key)) {
        return undefined;
    }

    persistSessionKey(key, loc);

    params.delete(SESSION_KEY_PARAM);
    const query = params.toString();
    history.replaceState(
        history.state,
        '',
        `${loc.pathname}${query ? `?${query}` : ''}${loc.hash}`
    );

    return key;
}

export async function authorize(sessionKey?: string) {
    const key = sessionKey ?? getSessionKey() ?? localStorage.getItem('sessionKey');

    const data = { sessionKey: key };
    const response = await post<AuthResponse, typeof data>('Auth/authorize', data);
    return response.data;
}

/**
 * `brandId` is the brand whose login page the credentials are being presented to. The API uses
 * it as a scope check, not to decide the resulting session: the brand on the response always
 * comes from the user record (an Admin at brand 1 signing in on a brand-363 host still comes
 * back as brand 1). Getting it wrong therefore does not mis-scope anyone — it just rejects the
 * login outright.
 *
 * This used to be hardcoded to 1. Admin/Owner/TeamMember pass the check against brand 1 anyway,
 * so the bug was invisible for those roles, but GroupAdmin, Client and Assistant are scoped
 * strictly to their own brand and got a flat 401 — they could not sign in through the React form
 * at all, on any host. Verified live against test.hd.pics: brandId 1 -> 401 for those three,
 * their own brand -> 200; every role succeeds with the host's brand.
 *
 * The caller resolves the host's brand via `ensureHostInfo()` (host.hd.pics), which the login
 * path already awaits. Falls back to 1 when the host lookup is unavailable, matching the old
 * behaviour rather than failing closed.
 */
export async function login(email: string, password: string, brandId: number = 1) {
    const data = {
        userName: email,
        password,
        brandId,
        sScreenRes: String(window.screen.width + 'x' + window.screen.height)
    };
    const response = await post<AuthResponse, typeof data>('Auth/login', data);
    return response.data;
}

export async function refresh(data: { accessToken?: string; refreshToken?: string }) {
    const response = await post<AuthResponse, typeof data>('auth/refresh-token', data);
    return response.data;
}
